import React, { useState, useEffect } from 'react';
import './App.css';
import AgePopup from './AgePopup';
import ModulesPage from './ModulesPage';
import RegistrationPage from './RegistrationPage';
import DashboardPage from './components/DashboardPage';
import LoginPage from './LoginPage';
import { supabase } from './supabaseClient';
import { INTELLECT_BASE_URL, INTELLECT_TASKS_URL } from './config/urls';
import DemoPage from './DemoPage';

// ===== ДАННЫЕ ДЛЯ РАДАР-ДИАГРАММЫ (пример отчёта в хиро) =====
const RADAR_AXES = [
  { label: 'Речь', value: 0.82 },
  { label: 'Слух', value: 0.95 },
  { label: 'Зрение', value: 0.90 },
  { label: 'Мышление', value: 0.62 },
  { label: 'Моторика', value: 0.78 },
  { label: 'Внимание', value: 0.70 },
  { label: 'Память', value: 0.88 },
  { label: 'Общение', value: 0.74 },
];

const SIZE = 260;
const CENTER = SIZE / 2;
const RADIUS = 92;
const RINGS = 4;

function pointAt(index, count, radius) {
  const angle = (Math.PI * 2 * index) / count - Math.PI / 2;
  return {
    x: CENTER + radius * Math.cos(angle),
    y: CENTER + radius * Math.sin(angle),
  };
}

// ===== РАДАР-ДИАГРАММА (SVG, без внешних библиотек) =====
const RadarChart = ({ glow = false }) => {
  const count = RADAR_AXES.length;

  const rings = Array.from({ length: RINGS }, (_, r) => {
    const ringRadius = (RADIUS * (r + 1)) / RINGS;
    return RADAR_AXES.map((_, i) => {
      const p = pointAt(i, count, ringRadius);
      return `${p.x},${p.y}`;
    }).join(' ');
  });

  const dataPoints = RADAR_AXES.map((a, i) => pointAt(i, count, RADIUS * a.value));
  const dataPolygon = dataPoints.map((p) => `${p.x},${p.y}`).join(' ');

  return (
    <svg
      viewBox={`-24 0 ${SIZE + 48} ${SIZE}`}
      className="radar-svg"
      role="img"
      aria-label="Радар-диаграмма оценки по 8 направлениям развития"
    >
      <defs>
        <linearGradient id="radarFill" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#312e81" stopOpacity={glow ? 0.5 : 0.35} />
          <stop offset="100%" stopColor="#7c9885" stopOpacity={glow ? 0.4 : 0.25} />
        </linearGradient>
        <filter id="radarGlow" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="4" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
      </defs>

      {rings.map((pts, i) => (
        <polygon key={i} points={pts} fill="none" stroke="#e6e4dc" strokeWidth={1} />
      ))}

      {RADAR_AXES.map((_, i) => {
        const p = pointAt(i, count, RADIUS);
        return (
          <line key={i} x1={CENTER} y1={CENTER} x2={p.x} y2={p.y} stroke="#e6e4dc" strokeWidth={1} />
        );
      })}

      <polygon
        points={dataPolygon}
        fill="url(#radarFill)"
        stroke="#312e81"
        strokeWidth={2}
        strokeLinejoin="round"
        filter={glow ? 'url(#radarGlow)' : undefined}
      />

      {dataPoints.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r={3.5} fill="#ffffff" stroke="#312e81" strokeWidth={2} />
      ))}

      {RADAR_AXES.map((a, i) => {
        const p = pointAt(i, count, RADIUS + 22);
        return (
          <text
            key={i}
            x={p.x}
            y={p.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontSize="9.5"
            fontWeight={600}
            fill="#6b6b64"
          >
            {a.label}
          </text>
        );
      })}
    </svg>
  );
};

// ===== ПРЕВЬЮ ОТЧЁТА СПРАВА ОТ ХИРО =====
const REPORT_RESULTS = [
  { label: 'Речь', status: 'Норма', tone: 'success', value: 82 },
  { label: 'Мышление', status: 'Требует внимания', tone: 'warning', value: 62 },
  { label: 'Слух', status: 'Норма', tone: 'success', value: 95 },
  { label: 'Внимание', status: 'В пределах нормы', tone: 'neutral', value: 70 },
];

const ReportDashboard = ({ onClick }) => (
  <div className="report-card" onClick={onClick} role="button" tabIndex={0}>
    <div className="report-card-header">
      <div className="report-card-header-left">
        <div className="report-card-icon">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
        </div>
        <div>
          <p className="report-card-title">Отчёт о развитии</p>
          <p className="report-card-subtitle">Матвей, 6 лет 3 мес.</p>
        </div>
      </div>
      <span className="report-card-badge">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
        </svg>
        Готов
      </span>
    </div>

    <div className="report-card-chart">
      <div className="report-card-chart-header">
        <p className="report-card-chart-title">8 направлений</p>
        <span className="report-card-chart-trend">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
            <polyline points="17 6 23 6 23 12" />
          </svg>
          78% общий уровень
        </span>
      </div>
      <div className="report-card-radar-wrap">
        <RadarChart glow />
      </div>
    </div>

    <div className="report-card-results">
      {REPORT_RESULTS.map((r) => (
        <div key={r.label} className="report-result-row">
          <div className="report-result-top">
            <span className="report-result-label">{r.label}</span>
            <span className={`report-result-pill tone-${r.tone}`}>{r.status}</span>
          </div>
          <div className="report-result-bar-track">
            <div className={`report-result-bar-fill tone-${r.tone}`} style={{ width: `${r.value}%` }} />
          </div>
        </div>
      ))}
    </div>
  </div>
);

// ===== ШАПКА =====
const SiteHeader = ({ session, onLoginClick, onRegisterClick, onDashboardClick, onLogout }) => (
  <header className="site-header">
    <div className="site-header-inner">
      
      {/* === НОВЫЙ ЛОГОТИП (Иконка сети + Текст) === */}
      <a href="#top" className="site-logo" aria-label="EZSpeech — на главную">
        <span className="site-logo-mark">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="5" r="2" />
            <circle cx="5" cy="19" r="2" />
            <circle cx="19" cy="19" r="2" />
            <line x1="12" y1="7" x2="6.5" y2="17" />
            <line x1="12" y1="7" x2="17.5" y2="17" />
            <line x1="7.5" y1="19" x2="16.5" y2="19" />
          </svg>
        </span>
        <span className="site-logo-text">EZSpeech</span>
      </a>
      {/* ========================================== */}

      <div className="site-header-auth">
        {session ? (
          <>
            <button type="button" className="site-header-login" onClick={onDashboardClick}>
              Личный кабинет
            </button>
            <button type="button" className="site-header-register" onClick={onLogout}>
              Выйти
            </button>
          </>
        ) : (
          <>
            <button type="button" className="site-header-login" onClick={onLoginClick}>
              Войти
            </button>
            <button type="button" className="site-header-register" onClick={onRegisterClick}>
              Регистрация
            </button>
          </>
        )}
      </div>
    </div>
  </header>
);

// ===== ХИРО =====
const Hero = ({ onStartClick, onDemoClick }) => (
  <section className="hero">
    <div className="hero-backdrop" aria-hidden="true" />
    <div className="hero-inner">

      <div className="hero-left">
        <span className="hero-badge">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 3v4M12 17v4M5 5l2.5 2.5M16.5 16.5L19 19M3 12h4M17 12h4M5 19l2.5-2.5M16.5 7.5L19 5" />
          </svg>
          Платформа оценки развития
        </span>

        <h1 className="hero-title">
          Прежде чем идти к специалисту — проверьте развитие ребёнка дома
        </h1>

        <p className="hero-subtitle">
          Развитие речи, мышления и понимания, слуховое и зрительное восприятие. Пройдите комплексную оценку по 8 направлениям
          в удобном темпе. Приходите на приём к специалисту не с тревогами, а с готовым отчётом и конкретными вопросами.
        </p>

        <div className="hero-actions">
          <button type="button" className="hero-cta-primary" onClick={onStartClick}>
            Выбрать модули для оценки
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
          </button>

          <button type="button" className="hero-cta-secondary" onClick={onDemoClick}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polygon points="10 8 16 12 10 16 10 8" />
            </svg>
            Смотреть демо видео
          </button>
        </div>

        <div className="hero-trust">
          <ul className="hero-trust-list">
            <li>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="hero-trust-icon">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
              </svg>
              Разработано совместно со специалистами ведущих центров
            </li>
            <li>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="hero-trust-icon">
                <rect x="3" y="11" width="18" height="11" rx="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Конфиденциально и безопасно
            </li>
          </ul>
          <p className="hero-trust-note">
            Точность оценки сопоставима с оценкой специалиста. Технология успешно
            апробирована в образовательных и медицинских учреждениях Москвы.
          </p>
        </div>
      </div>

      <div className="hero-right">
        <ReportDashboard onClick={onDemoClick} />
      </div>

    </div>
  </section>
);

// ===== БАННЕР "ИНТЕЛЛЕКТ" — отдельная секция между hero и футером =====
// Вынесено из футера намеренно: в футере это соседствовало с дисклеймером и
// логотипами партнёров и читалось как второстепенное. Как отдельная секция —
// это полноценное второе предложение на странице, а не "мелкий шрифт внизу".
const IntellectBanner = () => (
  <section className="intellect-banner">
    <div className="intellect-banner-inner">
      <p className="intellect-banner-text">
        А если хотите узнать не только то, что ребёнок уже знает, но и на что способен его ум — вам сюда:
      </p>
      <a
        className="intellect-banner-link"
        href={INTELLECT_BASE_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        Интеллект
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <line x1="5" y1="12" x2="19" y2="12" />
          <polyline points="12 5 19 12 12 19" />
        </svg>
      </a>
    </div>
  </section>
);

// ===== ФУТЕР: логотипы партнёров + дисклеймер =====
// Раньше это была отдельная полоса AwardsBar. Добавили обязательный юридический
// дисклеймер — раньше на этом лендинге его не было. Предложение про Интеллект
// сюда больше не входит — теперь это IntellectBanner выше.
const AWARDS = [
  {
    title: 'Московский Инновационный Кластер',
    logo: '/cluster.png',
  },
  {
    title: 'Премия «Открыто для всех»',
    logo: '/open.png',
  },
  {
    title: 'Московский центр инноваций в здравоохранении',
    logo: '/medcenter.png',
  },
];

const SiteFooter = () => (
  <footer className="site-footer">
    <div className="site-footer-inner">

      {/* ЛЕВАЯ КОЛОНКА: Логотипы, а ровно ПОД ними - копирайт */}
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <div className="site-footer-trust">
          {AWARDS.map((item) => (
            <div key={item.title} className="award-item">
              <img src={item.logo} alt={item.title} className="award-logo-img" />
            </div>
          ))}
        </div>
        <p className="site-footer-copyright" style={{ textAlign: 'left', marginTop: '20px' }}>
          © 2026 EZSpeech. Все права защищены.
        </p>
      </div>

      {/* ПРАВАЯ КОЛОНКА: Дисклеймер и ссылка на политику */}
      <div className="site-footer-legal" style={{ height: 'auto', overflow: 'visible', display: 'block', textAlign: 'left' }}>
        <p className="site-footer-disclaimer" style={{ marginBottom: '12px' }}>
          Результаты носят ориентировочный характер и не являются медицинским диагнозом, психолого-педагогическим или официальным заключением ПМПК. Для точной оценки развития ребёнка рекомендуем очную консультацию специалиста.
        </p>
        <a 
          href="/privacy-policy.pdf" 
          target="_blank" 
          rel="noopener noreferrer"
          style={{ color: '#6b6b64', fontSize: '13px', display: 'block', marginBottom: '12px' }}
        >
          Политика конфиденциальности (PDF)
        </a>
      </div>

    </div>
  </footer>
);

// ===== СТРАНИЦА ЦЕЛИКОМ =====
const App = () => {
  const [session, setSession] = useState(null);
  const [childProfile, setChildProfile] = useState(null);

  const [showPopup, setShowPopup] = useState(false);
  const [showModules, setShowModules] = useState(false);
  const [showRegistration, setShowRegistration] = useState(false);
  const [showDashboard, setShowDashboard] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [showDemo, setShowDemo] = useState(false);


  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isLoginPath = window.location.pathname === '/login' || window.location.pathname === '/login/';
    if (!isLoginPath) return;

    let cancelled = false;
    let authSubscription = null;

    const goToIntellect = () => {
      if (params.get('redirect') === 'intellect') {
        window.location.href = INTELLECT_TASKS_URL;
      }
    };

    supabase.auth.getSession().then(({ data }) => {
      if (cancelled) return;

      if (data.session) {
        goToIntellect();
      } else {
        setShowLogin(true);
        setShowModules(false);
        setShowRegistration(false);
        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, next) => {
          if (next) goToIntellect();
        });
        authSubscription = subscription;
      }
    });

    return () => {
      cancelled = true;
      authSubscription?.unsubscribe();
    };
  }, []);

     useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      // Если пользователь залогинен, проверяем, есть ли у него возраст в базе
      if (data.session?.user) {
        checkUserAge(data.session.user.id);
      }
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      if (next?.user) {
        checkUserAge(next.user.id);
      }
    });
    return () => subscription.unsubscribe();
  }, []);

  // Функция проверки возраста в Supabase
  const checkUserAge = async (userId) => {
    const { data, error } = await supabase
      .from('students')
      .select('age_group')
      .eq('id', userId)
      .single();

    if (data && data.age_group) {
      // Возраст уже сохранен в базе! Прячем попап.
      setChildProfile(data);
      setShowPopup(false);
    } else {
      // Возраста в базе нет. Показываем попап.
      setShowPopup(true);
    }
  };


  useEffect(() => {
    const saved = localStorage.getItem('ezspeech_user');
    if (saved) setChildProfile(JSON.parse(saved));
    localStorage.removeItem('current_student');
  }, []);

   const handleAgeSave = async (data) => {
    setChildProfile(data);
    setShowPopup(false);
    setShowModules(true);

    // Сохраняем возраст в базу данных (в таблицу students)
          const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { error } = await supabase
          .from('students')
          .update({ age_group: data.ageGroup })
          .eq('id', session.user.id);
          
        if (error) {
          console.error('❌ Ошибка сохранения возраста в БД:', error);
        } else {
          console.log('✅ Возраст успешно сохранен в БД!');
        }
      }
  };

  // Кнопка "Выбрать модули для оценки" в хиро
  const handleStartClick = () => {
    if (childProfile) {
      setShowModules(true);
    } else {
      setShowPopup(true);
    }
  };

  // Кнопка "Смотреть демо видео" в хиро
  const handleDemoClick = () => {
    setShowDemo(true);
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setShowDashboard(false);
  };

  if (showDashboard) {
    return <DashboardPage user={session?.user} onLogout={handleLogout} onBack={() => setShowDashboard(false)} />;
  }

  if (showRegistration) {
    return (
      <RegistrationPage
        onRegistered={() => setShowRegistration(false)}
        onGoToLogin={() => {
          setShowRegistration(false);
          setShowLogin(true);
        }}
      />
    );
  }

  if (showLogin) {
    return (
      <LoginPage
        onLoginSuccess={() => setShowLogin(false)}
        onGoToRegister={() => {
          setShowLogin(false);
          setShowRegistration(true);
        }}
      />
    );
  }

  if (showDemo) {
    return <DemoPage onBack={() => setShowDemo(false)} />;
  }

  if (showPopup) {
    return <AgePopup onSave={handleAgeSave} />;
  }

  if (showModules) {
    return <ModulesPage userData={childProfile} onBack={() => setShowModules(false)} onRequireLogin={() => { setShowModules(false); setShowLogin(true); }} />;
  }

  return (
    <div className="app-root" id="top">
      <SiteHeader
        session={session}
        onLoginClick={() => setShowLogin(true)}
        onRegisterClick={() => setShowRegistration(true)}
        onDashboardClick={() => setShowDashboard(true)}
        onLogout={handleLogout}
      />
      <main>
        <Hero onStartClick={handleStartClick} onDemoClick={handleDemoClick} />
        <IntellectBanner />
        <SiteFooter />
      </main>
    </div>
  );
};

export default App;